import { useCallback, useEffect, useRef, useState } from 'react';

import { FRONTEND_ENV } from '@frontend/shared/const/const.FRONTEND_ENV';

interface PickedFile {
  id: string;
  name: string;
  mimeType: string;
  url: string;
}

interface GoogleFilePickerProps {
  onFilesPicked: (files: PickedFile[]) => void;
  trigger: React.ReactNode;
  autoOpen?: boolean;
  // Email of the Google account that owns the backend OAuth connection. Passed
  // as a sign-in hint so the picker runs under the same account as the stored
  // refresh token — otherwise the user can pick files from a different Google
  // account and the sync worker 404s with drive.file scope.
  googleEmail?: string;
}

let gapiLoaded = false;

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

export function GoogleFilePicker({
  onFilesPicked,
  trigger,
  autoOpen,
  googleEmail,
}: GoogleFilePickerProps) {
  const [isReady, setIsReady] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tokenClientRef = useRef<any>(null);
  const accessTokenRef = useRef<string | null>(null);
  const didAutoOpenRef = useRef(false);

  const clientId = FRONTEND_ENV.VITE_GOOGLE_CLIENT_ID;
  const apiKey = FRONTEND_ENV.VITE_GOOGLE_API_KEY;

  useEffect(() => {
    if (!clientId || !apiKey) return;

    const init = async () => {
      await Promise.all([
        loadScript('https://apis.google.com/js/api.js'),
        loadScript('https://accounts.google.com/gsi/client'),
      ]);

      if (!gapiLoaded) {
        await new Promise<void>((resolve) => {
          gapi.load('picker', () => {
            gapiLoaded = true;
            resolve();
          });
        });
      }

      tokenClientRef.current = google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/drive.file',
        callback: () => {},
      });

      setIsReady(true);
    };

    init();
  }, [clientId, apiKey]);

  const showPicker = useCallback(
    (token: string) => {
      if (!clientId || !apiKey) return;

      const docsView = new google.picker.DocsView()
        .setIncludeFolders(false)
        .setSelectFolderEnabled(false)
        .setMode(google.picker.DocsViewMode.LIST);

      const picker = new google.picker.PickerBuilder()
        .addView(docsView)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .setOAuthToken(token)
        .setDeveloperKey(apiKey)
        .setAppId(clientId.split('-')[0])
        .setOrigin(window.location.protocol + '//' + window.location.host)
        .setSize(
          Math.min(1051, window.innerWidth - 50),
          Math.min(650, window.innerHeight - 50),
        )
        .setCallback((data: google.picker.ResponseObject) => {
          if (data.action === google.picker.Action.PICKED && data.docs) {
            const files: PickedFile[] = data.docs.map((doc) => ({
              id: doc.id,
              name: doc.name ?? '',
              mimeType: doc.mimeType ?? '',
              url: doc.url ?? '',
            }));
            onFilesPicked(files);
          }
          if (
            data.action === google.picker.Action.PICKED ||
            data.action === google.picker.Action.CANCEL
          ) {
            document.body.style.overflow = '';
          }
        })
        .setTitle('Select files to sync')
        .build();

      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      picker.setVisible(true);
      window.scrollTo(0, scrollY);

      // WORKAROUND: Google Picker sizing bug.
      // The Picker calculates its iframe dimensions at the moment setVisible(true)
      // is called. If the viewport layout is not fully settled (e.g. sidebar CSS
      // transition still in-flight, or Popover animation transforms creating a
      // temporary containing block for position:fixed elements), the Picker
      // captures stale dimensions and the iframe renders at 0px/1px width.
      // Manually resizing the browser window fixes it because the Picker listens
      // for window 'resize' events and recalculates. We replicate that here.
      // Two dispatches: one immediate (next frame) and one after CSS transitions
      // have settled (300ms > the 200ms sidebar transition duration).
      requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'));
      });
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    },
    [clientId, apiKey, onFilesPicked],
  );

  const openPicker = useCallback(() => {
    if (!tokenClientRef.current) return;

    if (accessTokenRef.current) {
      showPicker(accessTokenRef.current);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tokenClientRef.current.callback = (response: any) => {
      if (response.access_token) {
        accessTokenRef.current = response.access_token;
        showPicker(response.access_token);
      }
    };

    tokenClientRef.current.requestAccessToken(
      googleEmail ? { hint: googleEmail } : undefined,
    );
  }, [showPicker, googleEmail]);

  useEffect(() => {
    if (autoOpen && isReady && !didAutoOpenRef.current) {
      didAutoOpenRef.current = true;
      openPicker();
    }
  }, [autoOpen, isReady, openPicker]);

  if (!clientId) return null;

  return (
    <div onClick={isReady ? openPicker : undefined} className="inline-block">
      {trigger}
    </div>
  );
}
